<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container feedback-modal">
      <div class="modal-header">
        <div class="header-content">
          <i :class="headerIcon" class="header-icon"></i>
          <div>
            <h2>{{ headerTitle }}</h2>
            <p class="subtitle">Help us improve the platform</p>
          </div>
        </div>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- Success State -->
        <div v-if="submitted" class="success-state">
          <i class="fas fa-check-circle"></i>
          <h3>Thank you for your feedback!</h3>
          <p>We've received your submission and will review it shortly.</p>
          <button class="btn btn-primary" @click="$emit('close')">
            Close
          </button>
        </div>
        
        <!-- Form -->
        <form v-else @submit.prevent="submitFeedback">
          <!-- Feedback Type Selector -->
          <div class="type-selector">
            <button 
              v-for="type in feedbackTypes" 
              :key="type.value"
              type="button"
              class="type-btn"
              :class="{ active: form.feedback_type === type.value }"
              @click="form.feedback_type = type.value"
            >
              <i :class="type.icon"></i>
              <span>{{ type.label }}</span>
            </button>
          </div>
          
          <!-- Priority (for bugs) -->
          <div v-if="form.feedback_type === 'bug'" class="form-group priority-group">
            <label>Priority</label>
            <div class="priority-options">
              <label 
                v-for="priority in priorities" 
                :key="priority.value"
                class="priority-option"
                :class="{ selected: form.priority === priority.value }"
              >
                <input 
                  type="radio" 
                  v-model="form.priority" 
                  :value="priority.value"
                  hidden
                />
                <span class="priority-badge" :class="priority.value">
                  {{ priority.label }}
                </span>
              </label>
            </div>
          </div>
          
          <!-- Title -->
          <div class="form-group">
            <label for="title">
              {{ form.feedback_type === 'bug' ? 'Bug Summary' : 'Title' }}
              <span class="required">*</span>
            </label>
            <input 
              id="title"
              v-model="form.title"
              type="text"
              :placeholder="titlePlaceholder"
              required
            />
          </div>
          
          <!-- Description -->
          <div class="form-group">
            <label for="description">
              Description
              <span class="required">*</span>
            </label>
            <textarea 
              id="description"
              v-model="form.description"
              :placeholder="descriptionPlaceholder"
              rows="4"
              required
            ></textarea>
          </div>
          
          <!-- Bug-specific fields -->
          <template v-if="form.feedback_type === 'bug'">
            <div class="form-group">
              <label for="steps">Steps to Reproduce</label>
              <textarea 
                id="steps"
                v-model="form.steps_to_reproduce"
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                rows="3"
              ></textarea>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label for="expected">Expected Behavior</label>
                <textarea 
                  id="expected"
                  v-model="form.expected_behavior"
                  placeholder="What should have happened?"
                  rows="2"
                ></textarea>
              </div>
              
              <div class="form-group">
                <label for="actual">Actual Behavior</label>
                <textarea 
                  id="actual"
                  v-model="form.actual_behavior"
                  placeholder="What actually happened?"
                  rows="2"
                ></textarea>
              </div>
            </div>
          </template>
          
          <!-- Context Info -->
          <div class="context-info">
            <i class="fas fa-info-circle"></i>
            <span>
              We'll automatically include your current page URL, browser info, 
              and any recent errors to help diagnose issues.
            </span>
          </div>
          
          <!-- Error Display -->
          <div v-if="error" class="error-message">
            <i class="fas fa-exclamation-triangle"></i>
            {{ error }}
          </div>
          
          <!-- Submit -->
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" @click="$emit('close')">
              Cancel
            </button>
            <button 
              type="submit" 
              class="btn btn-primary"
              :disabled="submitting"
            >
              <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
              <span v-else>Submit {{ feedbackTypeLabel }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import observabilityAPI from '@/services/observabilityAPI';

export default {
  name: 'FeedbackModal',
  
  emits: ['close', 'submitted'],
  
  data() {
    return {
      form: {
        feedback_type: 'bug',
        title: '',
        description: '',
        priority: 'medium',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
      },
      feedbackTypes: [
        { value: 'bug', label: 'Bug', icon: 'fas fa-bug' },
        { value: 'feature', label: 'Feature', icon: 'fas fa-lightbulb' },
        { value: 'feedback', label: 'Feedback', icon: 'fas fa-comment' },
        { value: 'question', label: 'Question', icon: 'fas fa-question-circle' },
      ],
      priorities: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' },
      ],
      submitting: false,
      submitted: false,
      error: null,
    };
  },
  
  computed: {
    headerIcon() {
      const icons = {
        bug: 'fas fa-bug',
        feature: 'fas fa-lightbulb',
        feedback: 'fas fa-comment',
        question: 'fas fa-question-circle',
      };
      return icons[this.form.feedback_type] || 'fas fa-comment';
    },
    
    headerTitle() {
      const titles = {
        bug: 'Report a Bug',
        feature: 'Request a Feature',
        feedback: 'Share Feedback',
        question: 'Ask a Question',
      };
      return titles[this.form.feedback_type] || 'Submit Feedback';
    },
    
    feedbackTypeLabel() {
      return this.feedbackTypes.find(t => t.value === this.form.feedback_type)?.label || 'Feedback';
    },
    
    titlePlaceholder() {
      const placeholders = {
        bug: 'Brief description of the bug...',
        feature: 'Brief description of the feature...',
        feedback: 'Brief summary...',
        question: 'Your question...',
      };
      return placeholders[this.form.feedback_type] || 'Brief summary...';
    },
    
    descriptionPlaceholder() {
      const placeholders = {
        bug: 'Describe the bug in detail. What were you trying to do? What went wrong?',
        feature: 'Describe the feature you\'d like. What problem would it solve?',
        feedback: 'Share your thoughts, suggestions, or observations...',
        question: 'Provide any context that might help us answer your question...',
      };
      return placeholders[this.form.feedback_type] || 'Provide details...';
    },
  },
  
  methods: {
    async submitFeedback() {
      this.error = null;
      this.submitting = true;
      
      try {
        const feedback = await observabilityAPI.submitFeedback(this.form);
        this.submitted = true;
        this.$emit('submitted', feedback);
      } catch (err) {
        console.error('Failed to submit feedback:', err);
        this.error = err.response?.data?.detail || 
                     err.response?.data?.message || 
                     'Failed to submit feedback. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.feedback-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px;
  background: linear-gradient(135deg, #fc8181 0%, #f56565 100%);
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  font-size: 28px;
  opacity: 0.9;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-header .subtitle {
  margin: 4px 0 0;
  font-size: 12px;
  opacity: 0.9;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 40px 20px;
}

.success-state i {
  font-size: 64px;
  color: #68d391;
  margin-bottom: 16px;
}

.success-state h3 {
  margin: 0 0 8px;
  font-size: 20px;
  color: #2d3748;
}

.success-state p {
  color: #718096;
  margin-bottom: 24px;
}

/* Type Selector */
.type-selector {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.type-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.type-btn:hover {
  border-color: #cbd5e0;
  background: #edf2f7;
}

.type-btn.active {
  border-color: #667eea;
  background: #ebf4ff;
}

.type-btn i {
  font-size: 20px;
  color: #718096;
}

.type-btn.active i {
  color: #667eea;
}

.type-btn span {
  font-size: 12px;
  font-weight: 500;
  color: #4a5568;
}

/* Priority Group */
.priority-group {
  margin-bottom: 16px;
}

.priority-options {
  display: flex;
  gap: 8px;
}

.priority-option {
  cursor: pointer;
}

.priority-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.priority-badge.low {
  background: #f0fff4;
  color: #276749;
  border-color: #c6f6d5;
}

.priority-badge.medium {
  background: #fefcbf;
  color: #744210;
  border-color: #faf089;
}

.priority-badge.high {
  background: #fed7d7;
  color: #c53030;
  border-color: #feb2b2;
}

.priority-badge.critical {
  background: #742a2a;
  color: white;
  border-color: #742a2a;
}

.priority-option.selected .priority-badge {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Form Groups */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 6px;
}

.form-group label .required {
  color: #e53e3e;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 60px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Context Info */
.context-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #ebf8ff;
  border-radius: 6px;
  margin-bottom: 16px;
}

.context-info i {
  color: #3182ce;
  margin-top: 2px;
}

.context-info span {
  font-size: 12px;
  color: #2c5282;
  line-height: 1.5;
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fed7d7;
  color: #c53030;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 13px;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #667eea;
  color: white;
  border: none;
}

.btn-primary:hover {
  background: #5a67d8;
}

.btn-primary:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f7fafc;
}

@media (max-width: 480px) {
  .type-selector {
    flex-wrap: wrap;
  }
  
  .type-btn {
    flex: 0 0 calc(50% - 4px);
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
